import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HealthFacilityField } from "../../../../../enums/healthFacilityField";
import { LocalStorageKeys } from "../../../../../enums/localStorageKeys";
import { fetchHealthFacilityFromFirebase } from "../../../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getItemFromLocalStorage } from "../../../../../utils/localStorageHelper";

/**
 * Resolves the preferred facility id from auth context or local storage.
 *
 * @return The facility id when available, otherwise an empty string.
 */
const resolvePreferredFacilityId = (): string =>
  getCurrentFacilityId() ||
  getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ||
  "";

/**
 * Loads healthcare facilities and tracks the selected facility for POC configuration.
 *
 * @return Facility options, selection state, and loading flag.
 */
export function useHealthFacilitySelector() {
  const dispatch = useDispatch<AppDispatch>();
  const { data: healthFacilities, loading } = useSelector(
    (state: RootState) => state.healthFacility,
  );
  const [selectedFacilityId, setSelectedFacilityId] = useState("");

  useEffect(() => {
    dispatch(fetchHealthFacilityFromFirebase());
  }, [dispatch]);

  useEffect(() => {
    if (healthFacilities.length === 0) return;

    setSelectedFacilityId((current) => {
      if (
        current &&
        healthFacilities.some(
          (facility) =>
            (facility[HealthFacilityField.ID] ?? facility.DocId) === current,
        )
      ) {
        return current;
      }

      const preferredId = resolvePreferredFacilityId();
      const preferredMatch = healthFacilities.find(
        (facility) =>
          (facility[HealthFacilityField.ID] ?? facility.DocId) === preferredId,
      );
      if (preferredMatch) {
        return (
          preferredMatch[HealthFacilityField.ID] ?? preferredMatch.DocId ?? ""
        );
      }

      const firstFacility = healthFacilities[0];
      return firstFacility[HealthFacilityField.ID] ?? firstFacility.DocId ?? "";
    });
  }, [healthFacilities]);

  const options = useMemo(
    () =>
      healthFacilities.map((facility) => ({
        value: facility[HealthFacilityField.ID] ?? facility.DocId ?? "",
        label: facility[HealthFacilityField.NAME] ?? "Unknown",
      })),
    [healthFacilities],
  );

  return {
    options,
    selectedFacilityId,
    setSelectedFacilityId,
    loading,
  };
}
